import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Calendar, Clock, Users, Target, Lightbulb, TrendingUp } from 'lucide-react';
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { caseStudies } from "../data/portfolio";

export default function CaseStudyDetail() {
  const { slug } = useParams<{ slug: string }>();
  const study = caseStudies.find(s => s.slug === slug);

  if (!study) {
    return <Navigate to="/404" replace />;
  }

  const nextStudy = caseStudies[caseStudies.findIndex(s => s.slug === slug) + 1] || caseStudies[0];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-32 md:pt-40 pb-20"
    >
      {/* Back Button */}
      <div className="px-4 mb-8">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/case-studies"
            className="inline-flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to case studies</span>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="px-4 mb-16">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center space-x-2 mb-6">
              <span className={`px-3 py-1 rounded-full text-sm ${
                study.category === 'work' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-blue-100 text-blue-700'
              }`}>
                {study.category === 'work' ? 'Client Work' : 'Research'}
              </span>
              <span className="text-muted-foreground">{study.year}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl text-foreground mb-4 leading-tight">
              {study.title}
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              {study.subtitle}
            </p>

            {/* Project Meta */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-purple-600" />
                <div>
                  <div className="text-sm text-muted-foreground">Year</div>
                  <div className="font-medium">{study.year}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-purple-600" />
                <div>
                  <div className="text-sm text-muted-foreground">Duration</div>
                  <div className="font-medium">{study.duration}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4 text-purple-600" />
                <div>
                  <div className="text-sm text-muted-foreground">Role</div>
                  <div className="font-medium">{study.role}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4 text-purple-600" />
                <div>
                  <div className="text-sm text-muted-foreground">Team</div>
                  <div className="font-medium text-sm">{study.team.join(', ')}</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      <motion.section
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="px-4 mb-16"
      >
        <div className="max-w-6xl mx-auto">
          <div className="aspect-[16/10] overflow-hidden rounded-2xl">
            <ImageWithFallback
              src={study.image}
              alt={study.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </motion.section>

      {/* Content */}
      <div className="px-4">
        <div className="max-w-4xl mx-auto space-y-16">
          {/* Summary */}
          <motion.section
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl mb-6">Project Summary</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {study.summary}
            </p>
          </motion.section>

          {/* Problem & Solution */}
          <motion.section
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid md:grid-cols-2 gap-12"
          >
            <div>
              <h3 className="text-xl mb-4 flex items-center space-x-2">
                <Target className="w-5 h-5 text-red-500" />
                <span>The Problem</span>
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {study.problem}
              </p>
            </div>
            
            <div>
              <h3 className="text-xl mb-4 flex items-center space-x-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                <span>The Solution</span>
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {study.solution}
              </p>
            </div>
          </motion.section>

          {/* Impact */}
          <motion.section
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl mb-8 flex items-center space-x-2">
              <TrendingUp className="w-6 h-6 text-green-500" />
              <span>Impact & Results</span>
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {study.outcomes.map((outcome, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-xl"
                >
                  <div className="text-3xl font-medium text-purple-600 mb-2">
                    {outcome.value}
                  </div>
                  <div className="font-medium mb-1">{outcome.metric}</div>
                  <div className="text-sm text-muted-foreground">
                    {outcome.description}
                  </div>
                </motion.div>
              ))}
            </div>
            
            <ul className="space-y-2">
              {study.impact.map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-center space-x-3 text-muted-foreground"
                >
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span>{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.section>

          {/* Process */}
          <motion.section
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl mb-8">Design Process</h2>
            
            <div className="space-y-8">
              {study.process.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="border-l-2 border-purple-200 pl-6 relative"
                >
                  <div className="absolute -left-2 top-0 w-4 h-4 bg-purple-600 rounded-full" />
                  <h3 className="text-lg mb-2">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Key Learnings */}
          <motion.section
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl mb-6">Key Learnings</h2>
            <div className="space-y-4">
              {study.keyLearnings.map((learning, index) => (
                <motion.div
                  key={index}
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start space-x-3 p-4 bg-muted/30 rounded-xl"
                >
                  <Lightbulb className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <p className="text-muted-foreground leading-relaxed">{learning}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Tags */}
          <motion.section
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-lg mb-4">Skills & Tools</h3>
            <div className="flex flex-wrap gap-2">
              {study.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-muted px-3 py-1 rounded-full text-sm text-muted-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.section>

          {/* Next Project */}
          <motion.section
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="border-t border-border pt-16"
          >
            <h3 className="text-lg mb-6">Next Project</h3>
            <Link
              to={`/case-studies/${nextStudy.slug}`}
              className="block group hover:transform hover:scale-[1.02] transition-all duration-300"
            >
              <div className="bg-card backdrop-blur-xl border border-border rounded-2xl p-6 hover:shadow-lg hover:shadow-purple-500/10 transition-all">
                <div className="flex flex-col md:flex-row md:items-center md:space-x-6 space-y-4 md:space-y-0">
                  <div className="md:w-32 md:h-20 flex-shrink-0">
                    <div className="aspect-[3/2] md:aspect-auto md:h-full overflow-hidden rounded-lg">
                      <ImageWithFallback
                        src={nextStudy.image}
                        alt={nextStudy.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        nextStudy.category === 'work' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {nextStudy.category === 'work' ? 'Client Work' : 'Research'}
                      </span>
                    </div>
                    <h4 className="text-lg group-hover:text-purple-600 transition-colors mb-1">
                      {nextStudy.title}
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      {nextStudy.subtitle}
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          </motion.section>
        </div>
      </div>
    </motion.div>



  );
}
